import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useI18n } from "./i18n";

const ContentContext = createContext({
  content: null,
  loading: true,
  reload: () => {},
  tc: (path, fallback) => fallback || "",
});

/**
 * Provider that fetches CMS content from `/api/content/landing` and exposes
 * a `tc(path, fallback)` helper. `path` is a dotted path into the content
 * doc (e.g. "nav.diagnostics", "footer.tagline"). `fallback` is used when
 * the path is missing or content has not loaded yet.
 *
 * Bilingual fields ({ja, en}) are auto-resolved using the current i18n lang.
 */
export function ContentProvider({ children }) {
  const { lang } = useI18n();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    return api
      .get("/content/landing", { params: { lang } })
      .then((r) => setContent(r.data))
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * Resolve a bilingual CMS value strictly to the *current* language.
   * Returns undefined if the value isn't translated for that language —
   * this lets `tc()` fall back to its i18n-supplied default instead of
   * silently showing English to a Korean user, etc.
   */
  const resolveBilingual = useCallback(
    (val) => {
      if (val == null) return undefined;
      if (typeof val === "string") return val;
      if (typeof val === "object" && val[lang] !== undefined && val[lang] !== "")
        return val[lang];
      return undefined;
    },
    [lang]
  );

  const tc = useCallback(
    (path, fallback = "") => {
      if (!content) return fallback;
      const parts = path.split(".");
      let cur = content;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return fallback;
      }
      const resolved = resolveBilingual(cur);
      return resolved !== undefined ? resolved : fallback;
    },
    [content, resolveBilingual]
  );

  return (
    <ContentContext.Provider value={{ content, loading, reload, tc }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}
